import { stopEventPropagation } from 'tldraw';
import { syncPinFrameMembershipForShapes } from './frameMembership';

export const pinCardDraggingIds = new Set();
export const pinCardSyncingIds = new Set();

/** Screen-space px before a press becomes a drag (vs click-to-open). */
export const BOARD_PIN_CLICK_MOVE_THRESHOLD_PX = 5;

function isMovableBoardPinShape(shape) {
  return shape?.type === 'pin-card' || shape?.type === 'suggest-pin' || shape?.type === 'conversation-pin';
}

export function registerPinCardDragGuard(editor) {
  return editor.sideEffects.registerBeforeChangeHandler('shape', (prev, next, source) => {
    if (source !== 'user' || !prev || !next) return next;
    if (editor.getIsReadonly()) return prev;
    if (!isMovableBoardPinShape(prev) || !isMovableBoardPinShape(next)) return next;
    if (prev.x === next.x && prev.y === next.y) return next;
    if (pinCardDraggingIds.has(next.id) || pinCardSyncingIds.has(next.id)) return next;
    return prev;
  });
}

function isInteractivePinControl(target) {
  return Boolean(target?.closest?.('button, a, input, textarea, [data-pin-no-drag]'));
}

/**
 * Pin press handler. Header uses allowDrag; body uses allowDrag:false (select / open).
 * Callers pass shape-specific select/update/drop behavior.
 */
export function startBoardPinPointerInteraction(editor, event, options) {
  const {
    shapeId,
    shapeType,
    historyLabel = 'pin',
    getDragIds,
    onClick,
    onDrop,
    allowDrag = true,
    moveThresholdPx = BOARD_PIN_CLICK_MOVE_THRESHOLD_PX,
  } = options;

  if (editor.getInstanceState().isReadonly) {
    // Viewers can still click pins to open original targets — no drag/select.
    if (typeof onClick === 'function' && event.button === 0 && !isInteractivePinControl(event.target)) {
      stopEventPropagation(event);
      event.preventDefault();
      const handleEl = event.currentTarget;
      const startClientX = event.clientX;
      const startClientY = event.clientY;
      let movedPastClickThreshold = false;

      const handlePointerMove = moveEvent => {
        const dist = Math.hypot(
          moveEvent.clientX - startClientX,
          moveEvent.clientY - startClientY
        );
        if (dist >= moveThresholdPx) movedPastClickThreshold = true;
      };

      const handlePointerUp = upEvent => {
        handleEl.removeEventListener('pointermove', handlePointerMove);
        handleEl.removeEventListener('pointerup', handlePointerUp);
        handleEl.removeEventListener('pointercancel', handlePointerUp);
        if (!movedPastClickThreshold) onClick(upEvent);
      };

      handleEl.addEventListener('pointermove', handlePointerMove);
      handleEl.addEventListener('pointerup', handlePointerUp);
      handleEl.addEventListener('pointercancel', handlePointerUp);
      return true;
    }
    return false;
  }
  if (event.button !== 0) return false;
  if (isInteractivePinControl(event.target)) return false;

  stopEventPropagation(event);
  event.preventDefault();

  const selectedShapeIds = editor.getSelectedShapeIds();

  if (event.shiftKey) {
    if (selectedShapeIds.includes(shapeId)) {
      editor.markHistoryStoppingPoint(`shift deselecting ${historyLabel}`);
      editor.deselect(shapeId);
    } else {
      editor.markHistoryStoppingPoint(`shift selecting ${historyLabel}`);
      editor.setSelectedShapes([...selectedShapeIds, shapeId]);
    }
    return true;
  }

  if (!selectedShapeIds.includes(shapeId)) {
    editor.markHistoryStoppingPoint(`selecting ${historyLabel}`);
    editor.select(shapeId);
  }

  const handleEl = event.currentTarget;
  handleEl.setPointerCapture(event.pointerId);

  const currentShape = editor.getShape(shapeId);
  if (!currentShape) return true;

  const dragIds = typeof getDragIds === 'function'
    ? getDragIds()
    : [shapeId];
  const startShapes = new Map(
    dragIds
      .map(id => {
        const s = editor.getShape(id);
        return s ? [id, { x: s.x, y: s.y, type: s.type }] : null;
      })
      .filter(Boolean)
  );

  const startClientX = event.clientX;
  const startClientY = event.clientY;
  const startPage = editor.screenToPage({ x: event.clientX, y: event.clientY });
  let didDrag = false;
  let movedPastClickThreshold = false;

  const handlePointerMove = moveEvent => {
    const dist = Math.hypot(
      moveEvent.clientX - startClientX,
      moveEvent.clientY - startClientY
    );
    if (dist >= moveThresholdPx) {
      movedPastClickThreshold = true;
    }

    if (!allowDrag) return;

    if (!didDrag) {
      if (dist < moveThresholdPx) return;
      didDrag = true;
      for (const id of startShapes.keys()) pinCardDraggingIds.add(id);
    }

    const page = editor.screenToPage({ x: moveEvent.clientX, y: moveEvent.clientY });
    const dx = page.x - startPage.x;
    const dy = page.y - startPage.y;
    for (const [id, origin] of startShapes) {
      editor.updateShape({
        id,
        type: origin.type || shapeType,
        x: origin.x + dx,
        y: origin.y + dy,
      });
    }
  };

  const handlePointerUp = upEvent => {
    if (didDrag) {
      for (const id of startShapes.keys()) pinCardDraggingIds.delete(id);
    }
    if (handleEl.hasPointerCapture(upEvent.pointerId)) {
      handleEl.releasePointerCapture(upEvent.pointerId);
    }
    handleEl.removeEventListener('pointermove', handlePointerMove);
    handleEl.removeEventListener('pointerup', handlePointerUp);
    handleEl.removeEventListener('pointercancel', handlePointerUp);

    if (!didDrag) {
      if (!movedPastClickThreshold) {
        onClick?.(upEvent);
      }
      return;
    }

    const dropPoint = editor.screenToPage({
      x: upEvent.clientX,
      y: upEvent.clientY,
    });
    editor.run(() => {
      syncPinFrameMembershipForShapes(editor, [...startShapes.keys()], dropPoint);
    });
    onDrop?.(upEvent, [...startShapes.keys()]);
  };

  handleEl.addEventListener('pointermove', handlePointerMove);
  handleEl.addEventListener('pointerup', handlePointerUp);
  handleEl.addEventListener('pointercancel', handlePointerUp);
  return true;
}
